/**
 * Compare and Order Integers generator (6.NR.2.4).
 *
 * Compares and orders integers, including negatives, using number-line
 * reasoning (values further right are greater). All families are choice-based:
 * the true relation or extreme is computed from the integers themselves, so the
 * correct choice always matches the displayed values.
 *
 * Families:
 *   F1: which symbol makes the statement true   (< > =)
 *   F2: which integer is the greatest            (choice of values)
 *   F3: which integer is the least               (choice of values)
 */

import { rat } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Choice,
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "compare-order-integers" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
  /** How many values appear in the greatest/least families. */
  readonly listSize: number;
  /** Probability of forcing an equal pair in F1 (to exercise "="). */
  readonly pEqual: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], max: 12, listSize: 3, pEqual: 0.15 },
  standard: { families: ["F1", "F2", "F3"], max: 25, listSize: 3, pEqual: 0.2 },
  hard: { families: ["F1", "F2", "F3"], max: 60, listSize: 4, pEqual: 0.2 },
  challenge: { families: ["F2", "F3"], max: 120, listSize: 4, pEqual: 0.0 },
};

/** A signed integer in [-max, max] including possible negatives. */
function signedInt(rng: Rng, max: number): number {
  return rng.int(1, max) * rng.sign(0.5);
}

/** Pick `count` DISTINCT signed integers. */
function distinctInts(rng: Rng, max: number, count: number): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  let guard = 0;
  while (out.length < count && guard < 500) {
    guard++;
    const v = signedInt(rng, max);
    if (!seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

export class CompareOrderIntegersGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      if (family === "F1") {
        const a = signedInt(rng, cfg.max);
        const b = rng.bool(cfg.pEqual) ? a : signedInt(rng, cfg.max);
        const correctId = a < b ? "lt" : a > b ? "gt" : "eq";
        const symbol = a < b ? "<" : a > b ? ">" : "=";
        const choices: Choice[] = [
          { id: "lt", label: "<", latex: "<" },
          { id: "gt", label: ">", latex: ">" },
          { id: "eq", label: "=", latex: "=" },
        ];
        return {
          id: `${TOPIC}-${ctx.seed}-${family}`,
          generator: TOPIC,
          generatorVersion: VERSION,
          seed: ctx.seed,
          unit: topic.unit,
          topic: TOPIC,
          family,
          difficulty: ctx.difficulty,
          prompt: {
            text: `Which symbol makes the statement true?  ${a} __ ${b}`,
            latex: `${rat(a).toLatex()} \\;\\square\\; ${rat(b).toLatex()}`,
          },
          answerType: "choice",
          answer: { kind: "choice", value: correctId },
          steps: [
            {
              description:
                "On a number line, the value farther to the right is greater; farther left is less.",
              latex: `${rat(a).toLatex()} ${symbol} ${rat(b).toLatex()}`,
            },
          ],
          hints: [
            { level: 1, text: "Picture both numbers on a number line." },
            { level: 2, text: "Right is greater, left is less; -2 is greater than -5." },
          ],
          subskills: topic.subskills,
          choices,
        };
      }

      // F2 / F3: greatest or least of a list of distinct integers.
      const wantGreatest = family === "F2";
      const values = distinctInts(rng, cfg.max, cfg.listSize);
      if (values.length < cfg.listSize) continue;

      let targetIdx = 0;
      for (let i = 1; i < values.length; i++) {
        const cur = values[i] as number;
        const best = values[targetIdx] as number;
        if (wantGreatest ? cur > best : cur < best) targetIdx = i;
      }

      const options = values.map((v, i) => ({
        id: `c${i}`,
        v,
        correct: i === targetIdx,
      }));
      const shuffled = rng.shuffle(options);
      const choices: Choice[] = shuffled.map((o) => ({ id: o.id, latex: rat(o.v).toLatex() }));
      const correctId = (shuffled.find((o) => o.correct) as { id: string }).id;

      const sortedAsc = [...values].sort((x, y) => x - y);
      const listText = values.join(", ");

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: {
          text: `Which integer is the ${wantGreatest ? "greatest" : "least"}?  ${listText}`,
          latex: values.map((v) => rat(v).toLatex()).join(",\\; "),
        },
        answerType: "choice",
        answer: { kind: "choice", value: correctId },
        steps: [
          {
            description:
              "Order the integers on a number line; the rightmost is greatest and the leftmost is least.",
            latex: sortedAsc.map((v) => rat(v).toLatex()).join(" < "),
          },
        ],
        hints: [
          { level: 1, text: "Line the numbers up on a number line from left to right." },
          {
            level: 2,
            text: "The most negative number is the least; the most positive is the greatest.",
          },
        ],
        subskills: topic.subskills,
        choices,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "choice" ? q.answer.value : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const compareOrderIntegers = new CompareOrderIntegersGenerator();
